import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Navigation, Star, Clock, MapPin, CheckCircle2, Phone, Timer, Package, KeyRound, Heart } from "lucide-react";
import RideMap from "../../components/app/RideMap";
import { TipDialog, TrustBadge } from "../../components/app/TipAndTrust";
import { publicApi, titleCase } from "../../lib/api";
import GoodsProgress from "../../components/app/GoodsProgress";

// Smoothly-ticking ETA: re-anchors to `etaSec` whenever a fresh GPS fix arrives,
// then counts down second-by-second so the number never jumps.
function useSmoothEta(etaSec) {
  const [remaining, setRemaining] = useState(etaSec ?? null);
  const endRef = useRef(null);
  useEffect(() => {
    if (etaSec == null) { endRef.current = null; setRemaining(null); return; }
    endRef.current = Date.now() + etaSec * 1000;
    setRemaining(etaSec);
    const t = setInterval(() => {
      const left = Math.max(0, Math.round((endRef.current - Date.now()) / 1000));
      setRemaining(left);
    }, 1000);
    return () => clearInterval(t);
  }, [etaSec]);
  return remaining;
}

function fmt(sec) {
  if (sec == null) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function TripShare() {
  const { token } = useParams();
  const [trip, setTrip] = useState(undefined); // undefined=loading, null=not found, {expired:true}
  const [rv, setRv] = useState(0);
  const [rvReview, setRvReview] = useState("");
  const [rvDone, setRvDone] = useState(false);
  const [rvBusy, setRvBusy] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipDone, setTipDone] = useState(false);
  const etaRemaining = useSmoothEta(trip && trip.active ? trip.eta_sec ?? null : null);

  const submitReceiverRating = async (stars) => {
    setRvBusy(true);
    try {
      await publicApi.rateTrip(token, { rating: stars, review: rvReview.trim() || undefined });
      setRvDone(true);
    } catch (e) {
      setRvDone(e?.response?.status === 409);
    } finally { setRvBusy(false); }
  };

  useEffect(() => {
    let on = true;
    const poll = () => publicApi.trip(token)
      .then((d) => { if (on) setTrip(d); })
      .catch((e) => { if (on) setTrip(e?.response?.status === 410 ? { expired: true } : null); });
    poll();
    const t = setInterval(poll, 5000);
    return () => { on = false; clearInterval(t); };
  }, [token]);

  if (trip === undefined) return <div className="flex min-h-screen items-center justify-center text-ink-soft">Loading live trip…</div>;
  if (trip === null || trip.expired) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 p-6 text-center" data-testid="public-trip-gone">
      {trip?.expired ? <Timer className="h-10 w-10 text-ink-muted" /> : <MapPin className="h-10 w-10 text-ink-muted" />}
      <h1 className="text-xl font-bold text-ink">{trip?.expired ? "Trip link expired" : "Trip not found"}</h1>
      <p className="text-ink-soft">{trip?.expired ? "This shared trip link is no longer available." : "This trip link is invalid or was turned off by the rider."}</p>
    </div>
  );

  const pickup = { lat: trip.pickup_lat, lng: trip.pickup_lng };
  const drop = { lat: trip.drop_lat, lng: trip.drop_lng };
  const driver = trip.active && trip.lat != null ? { lat: trip.lat, lng: trip.lng, name: trip.driver?.name } : null;
  const toPickup = trip.phase === "to_pickup";
  const d = trip.driver;
  const isGoods = trip.purpose === "goods";
  const rider = trip.active && !isGoods && trip.rider_lat != null ? { lat: trip.rider_lat, lng: trip.rider_lng, name: "Rider" } : null;
  const partnerWord = isGoods ? "delivery partner" : "driver";
  const initials = (d?.name || "D").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex min-h-screen flex-col bg-surface-50">
      <header className="flex items-center gap-2 border-b border-surface-200 bg-white px-5 py-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white">{isGoods ? <Package className="h-4 w-4" /> : <Navigation className="h-4 w-4" />}</span>
        <div><p className="text-sm font-bold text-ink">KohimaTaxi · {isGoods ? "Live delivery" : "Live trip"}</p><p className="text-xs text-ink-soft">{isGoods ? "Track your incoming package" : "Shared by the rider"}</p></div>
      </header>

      {isGoods && trip.status !== "cancelled" ? (
        <div className="border-b border-surface-200 bg-white px-5 py-3">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-ink-muted">Delivery journey</p>
          <GoodsProgress ride={{ status: trip.status, arrived_at: trip.arrived_at, proof_photo_id: trip.proof_uploaded ? "x" : null, drop_code_verified: trip.drop_code_verified }} receiver full />
        </div>
      ) : null}

      {isGoods && trip.active && !toPickup ? (
        <div className="flex items-center gap-3 border-b border-brand-200 bg-brand-50 px-5 py-3" data-testid="public-trip-arriving">
          <Clock className="h-5 w-5 shrink-0 text-brand" />
          <p className="text-sm font-bold text-brand">
            {(etaRemaining != null || trip.eta_min != null)
              ? `Your package is arriving in ~${etaRemaining != null ? Math.max(1, Math.round(etaRemaining / 60)) : trip.eta_min} min`
              : "Your delivery partner is on the way to you"}
            {trip.dist_km != null ? <span className="font-semibold text-brand/80"> · {trip.dist_km} km away</span> : null}
          </p>
        </div>
      ) : null}

      {isGoods && trip.active && trip.drop_code ? (
        <div className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-5 py-3" data-testid="public-trip-dropcode">
          <KeyRound className="h-5 w-5 shrink-0 text-amber-700" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">Your drop code</p>
            <p className="text-2xl font-black tracking-[0.3em] text-amber-900" data-testid="public-trip-dropcode-value">{trip.drop_code}</p>
          </div>
          <p className="ml-auto max-w-[45%] text-right text-xs text-amber-800">Give this code to the {partnerWord} when they hand over your package.</p>
        </div>
      ) : null}

      {isGoods && trip.active && trip.pickup_photo_url ? (
        <div className="flex items-center gap-3 border-b border-surface-200 bg-white px-5 py-3" data-testid="public-trip-pickup-photo">
          <img src={trip.pickup_photo_url} alt="Parcel collected by partner" className="h-16 w-16 shrink-0 rounded-xl border border-surface-200 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Partner's photo of your parcel</p>
            <p className="text-sm font-semibold text-ink">Your {partnerWord} collected this item.</p>
            <p className="text-xs text-ink-soft">Check it matches your parcel at hand-over.</p>
          </div>
        </div>
      ) : null}

      {isGoods && trip.active && trip.package_photo_url ? (
        <div className="flex items-center gap-3 border-b border-surface-200 bg-white px-5 py-3" data-testid="public-trip-package-photo">
          <img src={trip.package_photo_url} alt="Your package" className="h-16 w-16 shrink-0 rounded-xl border border-surface-200 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Your package</p>
            {(etaRemaining != null || trip.eta_min != null) ? (
              <p className="flex items-center gap-1.5 text-sm font-bold text-brand" data-testid="public-trip-package-eta">
                <Clock className="h-4 w-4" /> Arriving in ~{etaRemaining != null ? Math.max(1, Math.round(etaRemaining / 60)) : trip.eta_min} min
              </p>
            ) : (
              <p className="text-sm font-semibold text-ink">{toPickup ? "Partner is on the way to collect it." : "On the way to you."}</p>
            )}
            <p className="text-xs text-ink-soft">Confirm this is your parcel at hand-over.</p>
          </div>
        </div>
      ) : null}

      <div className="relative">
        <RideMap pickup={pickup} drop={drop} activeDriver={driver} rider={rider} route={trip.route || null} follow className="h-[75vh] w-full" />
        {trip.active ? (
          <div className="absolute left-4 right-4 top-4 z-[500] rounded-2xl bg-white/95 p-4 shadow-lg backdrop-blur" data-testid="public-trip-active">
            <div className="flex items-center gap-3">
              {d?.photo
                ? <img src={d.photo} alt={d.name} className="h-12 w-12 shrink-0 rounded-full object-cover" data-testid="public-trip-photo" />
                : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand/10 text-sm font-bold text-brand" data-testid="public-trip-photo">{initials}</span>}
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-ink">{d?.name || (isGoods ? "Your delivery partner" : "Your driver")}</p>
                <p className="truncate text-xs text-ink-soft">
                  {[d?.vehicle || titleCase(trip.vehicle_type || ""), d?.plate].filter(Boolean).join(" · ")}
                  {d?.rating ? <span className="ml-1 inline-flex items-center gap-0.5 text-amber-500"><Star className="h-3 w-3 fill-amber-400" /> {d.rating}</span> : null}
                </p>
                {d?.trust?.eligible ? <TrustBadge trust={d.trust} className="mt-1" /> : null}
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-3 py-1.5 text-sm font-bold text-brand" data-testid="public-trip-eta">
                <Clock className="h-4 w-4" /> {etaRemaining != null ? fmt(etaRemaining) : (toPickup ? "On the way" : "En route")}
              </span>
            </div>
            {!isGoods && d?.vehicle_photo ? (
              <div className="mt-3 rounded-xl bg-surface-50 p-2" data-testid="public-trip-vehicle">
                <div className="flex items-center gap-3">
                  <img src={d.vehicle_photo} alt="Vehicle" className="h-14 w-20 shrink-0 rounded-lg object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  <p className="text-xs text-ink-soft"><span className="font-semibold text-ink">{d?.vehicle || titleCase(trip.vehicle_type || "")}</span> · Plate <span className="rounded bg-ink px-1.5 py-0.5 font-bold tracking-wide text-white">{d?.plate}</span></p>
                </div>
                <p className="mt-1.5 text-[11px] font-medium text-emerald-700">Match the number plate with the cab before boarding.</p>
              </div>
            ) : null}
            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="min-w-0 flex-1 truncate text-xs text-ink-soft">{toPickup ? `${isGoods ? "Partner" : "Driver"} is heading to the pickup` : `On the way to ${isGoods ? "you" : "the destination"}`}{trip.dist_km != null ? ` · ${trip.dist_km} km away` : ""} · {trip.drop_address}</p>
              {d?.phone ? (
                <a href={`tel:${d.phone}`} data-testid="public-trip-call" className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-brand px-3.5 py-2 text-xs font-semibold text-white hover:bg-brand-600">
                  <Phone className="h-3.5 w-3.5" /> Call {partnerWord}
                </a>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="absolute left-4 right-4 top-4 z-[500] space-y-3 rounded-2xl bg-white/95 p-4 shadow-lg backdrop-blur" data-testid="public-trip-ended">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-brand" />
              <div><p className="font-semibold text-ink">{isGoods ? "Delivery completed" : "Trip ended"}</p><p className="text-xs text-ink-soft">{isGoods ? "Your package has been delivered." : "This trip is no longer live."}</p></div>
            </div>
            {trip.can_rate ? (
              (rvDone || trip.receiver_rating) ? (
                <div className="rounded-xl bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700" data-testid="public-rate-thanks">Thanks for your feedback!</div>
              ) : (
                <div className="rounded-xl border border-surface-200 p-3" data-testid="public-rate-card">
                  <p className="mb-2 text-sm font-semibold text-ink">How was your delivery?</p>
                  <div className="flex items-center gap-1.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} disabled={rvBusy} onClick={() => { setRv(n); submitReceiverRating(n); }} data-testid={`public-rate-star-${n}`} className="transition-transform hover:scale-110 disabled:opacity-50">
                        <Star className={`h-9 w-9 ${n <= rv ? "fill-amber-400 text-amber-400" : "text-surface-300"}`} />
                      </button>
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs text-ink-muted">Tap a star to rate — one tap is all it takes.</p>
                </div>
              )
            ) : null}
            {trip.can_tip && !trip.receiver_tip && !tipDone ? (
              <button onClick={() => setTipOpen(true)} data-testid="public-tip-open" className="flex w-full items-center justify-center gap-2 rounded-xl bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-100">
                <Heart className="h-4 w-4" /> Add a thank-you tip for {d?.name || "your partner"}
              </button>
            ) : null}
            {(trip.receiver_tip || tipDone) ? (
              <div className="flex items-center justify-center gap-1.5 rounded-xl bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-600" data-testid="public-tip-done"><Heart className="h-4 w-4 fill-rose-500" /> Tip sent — thank you!</div>
            ) : null}
          </div>
        )}
      </div>
      <TipDialog open={tipOpen} onOpenChange={setTipOpen} token={token} partnerName={d?.name} onDone={() => setTipDone(true)} />
    </div>
  );
}
